import { useEffect, useRef, useState } from 'react'
import { CheckCircle2 } from 'lucide-react'
import { Button } from '@klear/ui/components/button'
import { cn } from '@klear/ui/lib/utils'
import type { NextStep } from '@klear/schema/document-review-step'

export const APPROVE_KEY = 'approve'

const ARRIVAL_MS = 1000

const APPROVABLE_STEPS: readonly NextStep[] = ['readyToApprove', 'waitingOnYou']

export function canDecideStep(step: NextStep): boolean {
  return APPROVABLE_STEPS.includes(step) || step === 'waitingOnParent'
}

export type CommitHold = {
  step: NextStep
  pendingParent: boolean
  canEdit: boolean
  editing: boolean
}

const HOLD_BY_STEP: Record<NextStep, string | null> = {
  readyToApprove: null,
  waitingOnYou: null,
  waitingOnParent: 'Create records stays closed until the parent this document names is decided.',
  superseded: 'Another run replaced this one, so there is nothing left to decide here.',
  filedByLaterRun: 'A later run filed this document, so there is nothing left to decide here.',
  creatingRecords: 'Klear is creating the records, so there is nothing to decide.',
  processing: 'The document is still processing.',
  blocked: 'This run needs processing again, keying in, or dismissing.',
  dismissed: 'This document was dismissed, so there is nothing to decide.',
  filed: 'The records exist, so there is nothing left to decide.',
  filedAutomatically: 'Klear filed this document on its own, so there is nothing left to decide.',
  notProcessed: 'This document has not been processed yet.'
}

export function holdReason(hold: CommitHold): string | null {
  if (!hold.canEdit) return 'Deciding a review needs source-document edit. Ask a super admin or a CSM to take this one.'
  const byStep = HOLD_BY_STEP[hold.step]
  if (byStep !== null) return byStep
  if (hold.pendingParent) return HOLD_BY_STEP.waitingOnParent
  if (hold.editing) return 'Finish the correction you are typing first.'
  return null
}

export type ApproveRowProps = {
  holdReason: string | null
  outstanding: number
  approvableCount: number
  canEdit: boolean
  step: NextStep
  isApproving: boolean
  isRejecting: boolean
  onApprove: () => void
  onReject: () => void
  onNextOpen: () => void
}

function stateText(holdReason: string | null, outstanding: number, approvableCount: number): string {
  if (holdReason !== null) return holdReason
  if (outstanding === 1) return '1 value left to confirm'
  if (outstanding > 1) return `${outstanding} values left to confirm`
  if (approvableCount === 0) return 'Nothing to confirm'
  return approvableCount === 1 ? 'The value is confirmed' : `All ${approvableCount} values confirmed`
}

export function ApproveRow({
  holdReason,
  outstanding,
  approvableCount,
  canEdit,
  step,
  isApproving,
  isRejecting,
  onApprove,
  onReject,
  onNextOpen
}: ApproveRowProps) {
  const busy = isApproving || isRejecting
  const ready = holdReason === null && outstanding === 0 && !busy
  const wasReady = useRef(ready)
  const [arrived, setArrived] = useState(false)

  useEffect(() => {
    const becameReady = ready && !wasReady.current
    wasReady.current = ready
    if (!becameReady) return
    setArrived(true)
    const timer = window.setTimeout(() => setArrived(false), ARRIVAL_MS)
    return () => window.clearTimeout(timer)
  }, [ready])

  return (
    <div className="bg-card flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-lg border px-4 py-3">
      <p
        className={cn(
          'flex min-w-0 items-center gap-2 text-sm',
          ready ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground'
        )}
        aria-live="polite"
      >
        {ready && <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />}
        <span>{stateText(holdReason, outstanding, approvableCount)}</span>
        {outstanding > 0 && holdReason === null && (
          <Button variant="ghost" size="xs" onClick={onNextOpen}>
            Next to confirm
          </Button>
        )}
      </p>
      <div className="flex shrink-0 flex-row-reverse items-center gap-2">
        <Button
          size="sm"
          data-approval-key={APPROVE_KEY}
          disabled={!ready}
          onClick={onApprove}
          className={cn(arrived && 'animate-arrive motion-reduce:animate-none')}
        >
          {isApproving ? 'Creating…' : 'Create records'}
        </Button>
        <Button variant="outline" size="sm" onClick={onReject} disabled={!canEdit || !canDecideStep(step) || busy}>
          Reject…
        </Button>
      </div>
    </div>
  )
}
